<template>
  <section
    class="receiving-report hdg-report-theme"
    @focusin="onReportFocus"
  >
    <b-card class="receiving-controls">
      <div class="receiving-heading d-flex align-items-center mb-2">
        <span class="receiving-heading-icon report-icon-frame mr-1"><feather-icon
          icon="ActivityIcon"
          size="22"
          aria-hidden="true"
        /></span>
        <div>
          <h4 class="mb-25">
            {{ $t('receivingReport.title') }}
          </h4>
          <span class="receiving-heading-subtitle">{{ $t('receivingReport.subtitle') }}</span>
        </div>
      </div>
      <b-row class="receiving-scope">
        <b-col
          cols="12"
          md="6"
          lg="4"
        >
          <b-form-group
            :label="$t('receivingReport.agent')"
            label-for="receiving-agent"
          >
            <v-select
              v-if="!context && initializing"
              input-id="receiving-agent"
              :options="[]"
              :clearable="false"
              :searchable="false"
              aria-busy="true"
              loading
              disabled
            />
            <AgentSelector
              v-else-if="context && context.canSelectAgent"
              v-model="selectedAgent"
              input-id="receiving-agent"
              source="receivingReport"
              :auto-select-default="false"
              :emit-self-as-option="true"
              :clearable="false"
              :disabled="scopeLocked"
              @change="agentChanged"
            />
            <b-form-input
              v-else
              id="receiving-agent"
              :value="agent"
              readonly
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="4"
        >
          <b-form-group
            :label="$t('receivingReport.lottery')"
            label-for="receiving-lottery"
          >
            <v-select
              v-model="lottery"
              input-id="receiving-lottery"
              class="receiving-lottery-select"
              :options="lotteries"
              label="name"
              :clearable="false"
              :loading="!context && initializing"
              :aria-busy="!context && initializing ? 'true' : 'false'"
              :disabled="scopeLocked || !context"
              @input="lotteryChanged"
            >
              <template #option="option">
                <span class="receiving-lottery-option">
                  <span
                    v-if="!option.isYeekee && hasFlag(option.name)"
                    class="flag-icon flag-icon-rounded receiving-lottery-option-icon"
                    :class="getFlagIconClass(option.name)"
                    aria-hidden="true"
                  />
                  <span class="receiving-lottery-option-name">{{ option.name }}</span>
                </span>
              </template>
              <template #selected-option="option">
                <span class="receiving-lottery-option">
                  <span
                    v-if="!option.isYeekee && hasFlag(option.name)"
                    class="flag-icon flag-icon-rounded receiving-lottery-option-icon"
                    :class="getFlagIconClass(option.name)"
                    aria-hidden="true"
                  />
                  <span class="receiving-lottery-option-name">{{ option.name }}</span>
                </span>
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="12"
          lg="4"
        >
          <b-form-group
            label-for="receiving-round"
          >
            <template #label>
              <span class="receiving-scope-label">
                <span>{{ $t('receivingReport.round') }}</span>
                <span
                  v-if="report && !report.canEditCaps"
                  class="receiving-read-only"
                ><feather-icon
                  icon="EyeIcon"
                  size="12"
                  aria-hidden="true"
                />{{ $t('receivingReport.readOnly') }}</span>
              </span>
            </template>
            <v-select
              input-id="receiving-round"
              class="receiving-round-select"
              :value="roundId || null"
              :options="roundOptions"
              :reduce="option => option.value"
              label="text"
              :clearable="false"
              :searchable="false"
              :placeholder="$t('receivingReport.chooseRound')"
              :loading="(!context && initializing) || (loadingRounds && !roundId)"
              :aria-busy="(!context && initializing) || (loadingRounds && !roundId) ? 'true' : 'false'"
              :disabled="scopeLocked || loadingRounds || !lottery"
              @input="selectRound"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <div class="receiving-toolbar">
        <div class="receiving-view-tools">
          <b-form-group
            class="receiving-search-field"
            :label="$t('receivingReport.searchLabel')"
            label-for="receiving-search"
          >
            <div class="receiving-search">
              <feather-icon
                icon="SearchIcon"
                size="18"
                aria-hidden="true"
              />
              <b-form-input
                id="receiving-search"
                v-model="numberQuery"
                type="search"
                inputmode="numeric"
                maxlength="6"
                :placeholder="$t('receivingReport.search')"
                :disabled="initializing"
                @input="presentationChanged"
              />
            </div>
          </b-form-group>
          <b-form-group
            class="receiving-sort"
            :label="$t('receivingReport.sort')"
            label-for="receiving-sort"
          >
            <v-select
              v-model="sort"
              input-id="receiving-sort"
              :options="sortOptions"
              :reduce="option => option.value"
              label="text"
              :clearable="false"
              :searchable="false"
              :disabled="saving || initializing"
              @input="presentationChanged"
            />
          </b-form-group>
          <div class="receiving-full-filter">
            <b-form-checkbox
              v-model="onlyFull"
              class="m-0"
              inline
              :disabled="initializing"
              @change="presentationChanged"
            >
              {{ $t('receivingReport.onlyFull') }}
            </b-form-checkbox>
          </div>
        </div>
        <div class="receiving-data-tools">
          <div class="receiving-auto-refresh">
            <b-form-checkbox
              id="receiving-auto-refresh"
              v-model="autoRefresh"
              class="m-0"
              switch
              inline
              aria-labelledby="receiving-auto-label"
              aria-describedby="receiving-auto-status"
              :disabled="saving || !report"
              @change="scheduleRefresh"
            />
            <label
              for="receiving-auto-refresh"
              class="receiving-auto-copy"
            >
              <span id="receiving-auto-label">{{ $t('receivingReport.autoRefresh') }}</span>
              <small
                id="receiving-auto-status"
                role="status"
              >{{ autoRefreshStatus }}</small>
            </label>
          </div>
          <b-button
            variant="outline-secondary"
            :aria-label="loading ? $t('receivingReport.updating') : $t('receivingReport.refresh')"
            :disabled="loading || saving || !roundId || hasDirty"
            @click="fetchReport"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              :class="{ 'receiving-refreshing': loading }"
              aria-hidden="true"
            />{{ $t('receivingReport.refresh') }}
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="!report || loading || saving"
            @click="openHistory"
          >
            <feather-icon
              icon="ClockIcon"
              size="14"
              aria-hidden="true"
            />{{ $t('receivingReport.history') }}
          </b-button>
        </div>
      </div>
    </b-card>

    <b-alert
      :show="!!error && !capError"
      variant="danger"
    >
      <div
        class="p-1"
        role="alert"
      >
        {{ error }}
      </div>
    </b-alert>
    <b-card
      v-if="report || initialReportLoading"
      class="receiving-content"
      :aria-busy="initialReportLoading || loading ? 'true' : 'false'"
    >
      <ReceivingReportSkeleton
        v-if="initialReportLoading"
        :round="skeletonRound"
        :can-edit="skeletonCanEditCaps"
        :show-cap-source="skeletonAgent !== 'master'"
        :is-mobile="isMobile"
        :active-type="activeType"
        :has-filters="hasFilters"
        :filter-label="filterResult"
      />
      <template v-else>
        <div class="receiving-summary-bar">
          <div><small>{{ $t('receivingReport.bet') }}</small><strong>{{ money(report.acceptedAmount) }}</strong></div>
          <div><small>{{ $t('receivingReport.commission') }}</small><strong>{{ money(-report.commission) }}</strong></div>
          <div><small>{{ $t('receivingReport.income') }}</small><strong :class="report.income < 0 ? 'report-text-danger' : 'report-text-success'">{{ money(report.income) }}</strong></div>
          <div><small>{{ $t('receivingReport.maximum') }}</small><strong class="report-text-danger">{{ money(report.maximumLoss) }}</strong></div>
        </div>
        <div class="receiving-meta">
          <span>
            {{ report.agent }} ·
            <span class="receiving-lottery-name">
              <span
                v-if="!report.round.isYeekee && hasFlag(report.round.name)"
                class="flag-icon flag-icon-rounded receiving-lottery-icon"
                :class="getFlagIconClass(report.round.name)"
                aria-hidden="true"
              />
              <span>{{ report.round.name }}</span>
            </span>
            · {{ dateLabel(report.round.roundDate) }}
          </span>
          <div class="receiving-freshness">
            <span class="receiving-updated">{{ $t('receivingReport.updated') }} {{ timeLabel(report.generatedAt) }}</span>
            <b-button
              v-if="pendingReport"
              variant="outline-primary"
              size="sm"
              class="receiving-show-latest"
              :title="$t('receivingReport.newData')"
              :disabled="hasDirty || saving || historyVisible || numberVisible || swipePreviews.length > 0"
              @click="applyPending"
            >{{ $t('receivingReport.showLatest') }}</b-button>
            <span
              class="sr-only"
              role="status"
            >{{ pendingReport ? $t('receivingReport.newData') : loading ? $t('receivingReport.updating') : '' }}</span>
          </div>
        </div>
        <div class="receiving-explanation">
          <button
            type="button"
            class="receiving-explanation-toggle"
            :aria-expanded="explanationOpen ? 'true' : 'false'"
            aria-controls="receiving-calculation"
            @click="explanationOpen = !explanationOpen"
          >
            <feather-icon
              icon="ChevronRightIcon"
              size="14"
              :class="{ 'receiving-explanation-open': explanationOpen }"
              aria-hidden="true"
            />{{ $t('receivingReport.howCalculated') }}
          </button>
          <b-collapse
            id="receiving-calculation"
            v-model="explanationOpen"
          >
            <p>{{ $t('receivingReport.calculationHint') }}</p>
          </b-collapse>
        </div>
        <div
          v-if="hasFilters"
          class="receiving-filter-summary"
        >
          <div class="receiving-filter-actions">
            <span
              class="receiving-filter-count"
              role="status"
            ><feather-icon
              icon="FilterIcon"
              size="14"
              aria-hidden="true"
            /><span>{{ filterResult }}</span></span>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="receiving-clear-filters"
              @click="clearFilters"
            ><feather-icon
              icon="XIcon"
              size="14"
              aria-hidden="true"
            />{{ $t('receivingReport.clearFilters') }}</b-button>
          </div>
          <span class="receiving-filter-note">{{ $t('receivingReport.totalsUnfiltered') }}</span>
        </div>
        <p
          v-if="report.canEditCaps"
          class="receiving-cap-hint report-text-secondary"
        >{{ $t('receivingReport.capHint') }}</p>
        <div
          ref="tableSurface"
          class="receiving-table"
          :class="{ 'receiving-table-mobile': isMobile, 'receiving-native-horizontal': horizontalTimelineSupported && !isMobile }"
          :style="tableGeometry"
          v-on="horizontalWheelListeners"
        >
          <div class="receiving-sticky-titles">
            <div
              ref="titleViewport"
              class="receiving-header-viewport receiving-titles-viewport"
              @scroll.passive="onHeaderScroll"
            >
              <ReceivingReportTypeTabs
                v-if="isMobile"
                :options="viewTypes"
                :value="activeType"
                :show-counts="hasFilters"
                @input="selectMobileType"
                @step="stepMobileType"
              />
              <div
                v-else
                class="receiving-header-track"
              >
                <div class="receiving-header-window">
                  <div class="receiving-header-row">
                    <div
                      v-for="(column, index) in renderedTypes"
                      :key="column.betType"
                      class="receiving-header-cell receiving-type-title"
                      :style="headerColumnStyle(index)"
                    ><h4>{{ $t(column.label) }}</h4></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            ref="grid"
            class="receiving-grid-scroll"
            role="region"
            tabindex="0"
            :aria-label="$t('receivingReport.table')"
            @scroll.passive="onGridScroll"
          >
            <div class="receiving-grid">
              <ReceivingReportColumn
                v-for="column in renderedTypes"
                :key="isMobile ? 'mobile' : column.betType"
                ref="columns"
                :column="column"
                :draft="drafts[column.betType]"
                :dirty="drafts[column.betType] !== originals[column.betType]"
                :can-edit="report.canEditCaps"
                :show-cap-source="report.agent !== 'master'"
                :saving="saving"
                :scroll-top="scrollTop"
                :viewport-height="viewportHeight"
                :minimum-height="isMobile ? mobileTableMinHeight : 0"
                :type-options="isMobile ? viewTypes : []"
                :empty-message="emptyMessage"
                native-headers
                @cap="setDraft(column.betType, $event)"
                @details="openNumber(column, $event)"
              />
              <div
                v-if="swipePreviews.length"
                class="receiving-swipe-preview-viewport"
                :style="swipeViewportStyle"
                aria-hidden="true"
                inert
              >
                <div
                  v-for="preview in swipePreviews"
                  :key="preview.column.betType"
                  class="receiving-swipe-preview"
                  :data-preview-type="preview.column.betType"
                  :style="swipePreviewStyle(preview)"
                >
                  <ReceivingReportColumn
                    :column="preview.column"
                    :draft="drafts[preview.column.betType]"
                    :dirty="drafts[preview.column.betType] !== originals[preview.column.betType]"
                    :can-edit="report.canEditCaps"
                    :show-cap-source="report.agent !== 'master'"
                    :saving="saving"
                    :scroll-top="preview.scrollTop"
                    :viewport-height="viewportHeight"
                    :minimum-height="Math.max(0, swipeLayout.height - scrollTop + preview.scrollTop)"
                    :type-options="viewTypes"
                    :empty-message="emptyMessage"
                    :style="{ marginLeft: `${-preview.position.left}px` }"
                    native-headers
                    preview
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            class="receiving-sticky-labels"
            aria-hidden="true"
          >
            <div
              ref="labelsViewport"
              class="receiving-header-viewport receiving-labels-viewport receiving-swipe-area"
              @scroll.passive="onHeaderScroll"
            >
              <div class="receiving-header-track">
                <div class="receiving-header-window">
                  <div class="receiving-header-row">
                    <div
                      v-for="(column, index) in renderedTypes"
                      :key="column.betType"
                      class="receiving-header-cell"
                      :style="headerColumnStyle(index)"
                    >
                      <div class="receiving-grid-labels">
                        <span>{{ $t('receivingReport.number') }}</span><span>{{ $t('receivingReport.accepted') }}</span><span>{{ $t('receivingReport.payout') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="swipePreviews.length"
                class="receiving-swipe-preview-viewport"
                :style="swipeViewportStyle"
              >
                <div
                  v-for="preview in swipePreviews"
                  :key="preview.column.betType"
                  class="receiving-swipe-preview"
                  :style="swipePreviewStyle(preview, true)"
                >
                  <div
                    class="receiving-grid-labels"
                    :style="{ width: `${columnWidths[0]}px`, marginLeft: `${-preview.position.left}px` }"
                  >
                    <span>{{ $t('receivingReport.number') }}</span><span>{{ $t('receivingReport.accepted') }}</span><span>{{ $t('receivingReport.payout') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-show="hasHorizontalOverflow"
          ref="horizontalRail"
          class="receiving-horizontal-rail"
          :class="{ 'receiving-horizontal-rail-visible': tableInView }"
          :style="{ bottom: `${saveBarHeight}px` }"
          role="region"
          tabindex="0"
          :aria-label="$t('receivingReport.horizontalScroll')"
          @scroll.passive="onRailScroll"
          v-on="horizontalWheelListeners"
        >
          <div :style="{ width: `${horizontalWidth}px`, height: '1px' }" />
        </div>
        <div
          v-if="report.canEditCaps && (hasDirty || saving || capSaved)"
          ref="saveBar"
          class="receiving-edit-row receiving-edit-row-sticky"
          :class="{ 'receiving-edit-row-saved': capSaved }"
        >
          <div class="receiving-edit-status">
            <span
              v-if="capError"
              class="receiving-cap-error report-text-danger"
              role="alert"
            >{{ error }}</span>
            <span
              v-else-if="capSaved"
              class="report-text-success"
              role="status"
            ><feather-icon
              icon="CheckCircleIcon"
              size="16"
              aria-hidden="true"
            />{{ message }}</span>
            <span
              v-else
              class="report-text-warning"
              role="status"
            >{{ $t('receivingReport.unsaved') }}</span>
          </div>
          <div class="receiving-save-actions">
            <b-button
              v-if="capSaved"
              variant="flat-secondary"
              class="receiving-dismiss-save"
              :aria-label="$t('ปิด')"
              @click="message = ''"
            >
              <feather-icon
                icon="XIcon"
                size="16"
                aria-hidden="true"
              />
            </b-button>
            <template v-else>
              <b-button
                v-if="hasDirty"
                variant="flat-secondary"
                :disabled="saving"
                @click="discardCaps"
              >
                {{ $t('receivingReport.discard') }}
              </b-button>
              <b-button
                variant="primary"
                :disabled="!hasDirty || saving || loading"
                @click="saveCaps"
              >
                <b-spinner
                  v-if="saving"
                  small
                  class="mr-50"
                />{{ $t('receivingReport.save') }}
              </b-button>
            </template>
          </div>
        </div>
      </template>
    </b-card>
    <b-card
      v-else-if="!error"
      class="text-center report-text-secondary"
    >
      {{ $t('receivingReport.chooseLottery') }}
    </b-card>

    <b-modal
      v-model="historyVisible"
      :title="$t('receivingReport.history')"
      size="lg"
      hide-footer
      scrollable
      modal-class="hdg-report-theme"
      @hidden="history = []"
    >
      <div
        v-if="historyLoading"
        class="text-center p-2"
      >
        <b-spinner small />
      </div>
      <p
        v-else-if="historyError"
        class="report-text-danger"
        role="alert"
      >
        {{ historyError }}
      </p>
      <p
        v-else-if="!history.length"
        class="report-text-secondary"
      >
        {{ $t('receivingReport.noHistory') }}
      </p>
      <div
        v-for="entry in history"
        :key="entry.id"
        class="receiving-history-entry"
      >
        <strong>{{ entry.actor }}</strong> <small>{{ timeLabel(entry.createAt) }}</small>
        <div
          v-for="(change, index) in entry.changes"
          :key="index"
          class="mt-50"
        >
          {{ historyTypeLabel(change) }} {{ change.number || '' }}:
          {{ historyCapLabel(change, 'before') }} → {{ historyCapLabel(change, 'after') }}
        </div>
      </div>
    </b-modal>
    <b-modal
      v-model="numberVisible"
      :title="numberTitle"
      hide-footer
      modal-class="hdg-report-theme"
      @hidden="selectedNumber = null"
    >
      <template v-if="selectedNumber">
        <p class="report-text-secondary">
          {{ $t('receivingReport.todeHint') }}
        </p>
        <table class="table table-sm receiving-parts">
          <thead><tr><th>{{ $t('receivingReport.number') }}</th><th class="text-right">
            {{ $t('receivingReport.accepted') }}
          </th><th class="text-right">
            {{ $t('receivingReport.cap') }}
          </th></tr></thead>
          <tbody><tr
            v-for="part in selectedNumber.parts"
            :key="part.number"
          >
            <td>{{ part.number }}</td><td class="text-right">
              {{ money(part.acceptedAmount) }}
            </td><td class="text-right">
              {{ part.capAmount == null ? $t('receivingReport.unlimited') : money(part.capAmount) }}
            </td>
          </tr></tbody>
        </table>
      </template>
    </b-modal>
  </section>
</template>

<script>
import {
  BAlert, BButton, BCard, BCol, BCollapse, BFormCheckbox, BFormGroup, BFormInput, BModal, BRow, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import { countryFlagMixin } from '@core/mixins/ui/countryFlag'
import receivingService from '@/service/receivingReportService'
import {
  capInput, changedCaps, completeReceivingReport, matchesReceivingNumber, normalizeNumberQuery, receivingPageViewport, RECEIVING_ROW_HEIGHT, sortReceivingNumbers,
} from '@/utils/receivingReport'
import { money } from '@/utils/poyReport'
import receivingTypeSwipe from '@/utils/receivingTypeSwipe'
import ReceivingReportColumn from './ReceivingReportColumn.vue'
import ReceivingReportSkeleton from './ReceivingReportSkeleton.vue'
import ReceivingReportTypeTabs from './ReceivingReportTypeTabs.vue'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCol,
    BCollapse,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BModal,
    BRow,
    BSpinner,
    vSelect,
    AgentSelector,
    ReceivingReportColumn,
    ReceivingReportSkeleton,
    ReceivingReportTypeTabs,
  },
  mixins: [countryFlagMixin],
  data() {
    return {
      context: null,
      lottery: null,
      lotteries: [],
      rounds: [],
      roundId: '',
      selectedAgent: null,
      sort: 'loss',
      numberQuery: '',
      onlyFull: false,
      explanationOpen: false,
      report: null,
      pendingReport: null,
      scrollTop: 0,
      viewportHeight: 600,
      tableStickyTop: 0,
      tableTitleHeight: 44,
      tableLabelsHeight: 38,
      tableSummaryHeight: 230,
      columnWidths: [],
      swipePreviews: [],
      swipeLayout: { width: 0, left: 0, height: 0 },
      mobileTableMinHeight: 0,
      viewportFrame: null,
      focusFrame: null,
      horizontalWidth: 0,
      horizontalRange: 0,
      horizontalTimelineSupported: false,
      hasHorizontalOverflow: false,
      tableInView: false,
      saveBarHeight: 0,
      isMobile: false,
      positions: {},
      gridObserver: null,
      observedGrid: null,
      drafts: {},
      originals: {},
      activeType: 1,
      initializing: true,
      loading: false,
      loadingRounds: false,
      saving: false,
      error: '',
      message: '',
      autoRefresh: false,
      refreshTimer: null,
      requestId: 0,
      initializationId: 0,
      roundRequestId: 0,
      disposed: false,
      historyVisible: false,
      historyLoading: false,
      historyError: '',
      history: [],
      historyRequestId: 0,
      numberVisible: false,
      selectedNumber: null,
      numberTitle: '',
    }
  },
  computed: {
    agent() { return this.selectedAgent?.code || this.context?.agent || '' },
    scopeKey() { return `${this.agent}:${this.lottery?.huayId || ''}:${this.roundId}` },
    hasDirty() { return Object.keys(this.originals).some(key => this.drafts[key] !== this.originals[key]) },
    capError() { return Boolean(this.report?.canEditCaps && this.hasDirty && this.error) },
    capSaved() { return Boolean(this.message && !this.hasDirty && !this.saving) },
    initialReportLoading() { return !this.report && (this.initializing || this.loadingRounds || this.loading) },
    skeletonAgent() { return this.agent || this.$store.state.app.userData?.agent || '' },
    skeletonRound() {
      const requestedRoundId = this.roundId || (this.initializing ? this.$route.query.roundId : '')
      const selected = this.rounds.find(round => round.roundId === requestedRoundId)
      if (selected || !this.lottery) return selected || null
      // Keep the market's column layout while its rounds load, but never borrow
      // current-round editing placeholders for a requested historical round.
      return {
        ...this.lottery,
        isCurrent: Boolean(this.lottery.isCurrent && (!requestedRoundId || requestedRoundId === this.lottery.roundId)),
      }
    },
    skeletonCanEditCaps() {
      // Placeholder geometry only; actual editing still requires report.canEditCaps from the API.
      const user = this.$store.state.app.userData
      const ownAgent = this.context?.agent || user?.agent
      const abilities = user?.ability || []
      const master = ownAgent === 'master' && abilities.some(value => value.subject === 'all' && value.action === 'manage')
      const ownWriter = this.skeletonAgent === ownAgent
        && abilities.some(value => value.subject === 'riskCap' && ['update', 'manage'].includes(value.action))
      return this.skeletonAgent !== 'master' && Boolean(this.skeletonRound?.isCurrent && (master || ownWriter))
    },
    autoRefreshStatus() {
      if (!this.autoRefresh) return this.$t('receivingReport.autoRefreshOff')
      if (this.hasDirty || this.saving) return this.$t('receivingReport.autoRefreshEditing')
      if (this.historyVisible || this.numberVisible) return this.$t('receivingReport.autoRefreshInspecting')
      if (this.loading) return this.$t('receivingReport.updating')
      if (this.pendingReport) return this.$t('receivingReport.autoRefreshPending')
      return this.$t('receivingReport.autoRefreshOn')
    },
    scopeLocked() { return this.hasDirty || this.saving || this.initializing || this.loadingRounds },
    userKey() { const user = this.$store.state.app.userData; return `${user?.agent || ''}:${user?.username || ''}` },
    searchQuery() { return normalizeNumberQuery(this.numberQuery) },
    hasFilters() { return Boolean(this.searchQuery || this.onlyFull) },
    emptyMessage() {
      if (this.onlyFull) return this.$t(this.searchQuery ? 'receivingReport.noFullMatches' : 'receivingReport.noFullNumbers')
      return this.$t('receivingReport.noMatches')
    },
    sortedTypes() {
      return (this.report?.types || []).map(column => Object.freeze({
        ...column, numbers: Object.freeze(sortReceivingNumbers(column.numbers, this.sort)),
      }))
    },
    viewTypes() {
      return this.hasFilters ? this.sortedTypes.map(column => Object.freeze({
        ...column,
        numbers: Object.freeze(column.numbers.filter(item => (!this.onlyFull || item.isFull)
          && matchesReceivingNumber(item, column.betType, this.searchQuery))),
      })) : this.sortedTypes
    },
    renderedTypes() { return this.isMobile ? this.viewTypes.filter(column => column.betType === this.activeType) : this.viewTypes },
    tableGeometry() {
      return {
        '--receiving-sticky-top': `${this.tableStickyTop}px`,
        '--receiving-title-height': `${this.tableTitleHeight}px`,
        '--receiving-labels-height': `${this.tableLabelsHeight}px`,
        '--receiving-summary-height': `${this.tableSummaryHeight}px`,
        '--receiving-horizontal-range': `${this.horizontalRange}px`,
      }
    },
    horizontalWheelListeners() { return this.horizontalTimelineSupported ? {} : { wheel: this.onHorizontalWheel } },
    swipeViewportStyle() { return { left: `${this.swipeLayout.left}px`, width: `${this.swipeLayout.width}px` } },
    matchedNumbers() { return this.renderedTypes.reduce((sum, column) => sum + column.numbers.length, 0).toLocaleString('en-US') },
    filterResult() {
      const parts = []
      if (this.searchQuery) parts.push(this.$t('receivingReport.searchFilter', { number: this.searchQuery }))
      if (this.onlyFull) parts.push(this.$t('receivingReport.onlyFull'))
      parts.push(this.$t('receivingReport.matches', { count: this.matchedNumbers }))
      return parts.join(' · ')
    },
    positionKey() { return this.isMobile ? String(this.activeType) : 'desktop' },
    sortOptions() {
      return [{ value: 'loss', text: this.$t('receivingReport.sortLoss') }, { value: 'number', text: this.$t('receivingReport.sortNumber') }]
    },
    roundOptions() {
      const labels = this.rounds.map(round => (round.roundDate ? new Date(round.roundDate).toLocaleDateString(this.$i18n.locale === 'th' ? 'th-TH' : 'en-GB', {
        day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok',
      }) : round.roundId))
      const counts = new Map()
      labels.forEach(label => counts.set(label, (counts.get(label) || 0) + 1))
      return this.rounds.map((round, index) => ({
        value: round.roundId,
        text: `${labels[index]}${counts.get(labels[index]) > 1 ? ` · ${round.roundId}` : ''}${round.isCurrent ? ` · ${this.$t('receivingReport.current')}` : ''}`,
      }))
    },
  },
  watch: {
    userKey() {
      this.invalidateScope()
      this.roundRequestId += 1
      this.context = null
      this.selectedAgent = null
      this.lottery = null
      this.roundId = ''
      this.rounds = []
      this.lotteries = []
      this.initialize()
    },
    historyVisible(value) { if (!value) this.historyRequestId += 1; this.scheduleRefresh() },
    numberVisible() { this.scheduleRefresh() },
    viewTypes: 'cancelSwipe',
    isMobile: 'cancelSwipe',
    activeType() { if (this.typeSwipe) this.typeSwipe.sync() },
    '$store.state.appConfig.layout.navbar.type': 'queuePageMeasure',
  },
  mounted() {
    this.horizontalTimelineSupported = Boolean(window.CSS?.supports('scroll-timeline: --receiving-x x')
      && window.CSS.supports('timeline-scope: --receiving-x') && window.CSS.supports('animation-timeline: --receiving-x'))
    document.addEventListener('visibilitychange', this.scheduleRefresh)
    document.addEventListener('transitionend', this.onNavbarTransition)
    document.addEventListener('transitioncancel', this.onNavbarTransition)
    window.addEventListener('beforeunload', this.beforeUnload)
    window.addEventListener('resize', this.syncViewport)
    window.addEventListener('scroll', this.queuePageMeasure, { passive: true })
    this.syncViewport()
    this.initialize()
  },
  beforeRouteUpdate(to, from, next) {
    const sameScope = to.query.agent === this.agent && to.query.huayId === this.lottery?.huayId && to.query.roundId === this.roundId
    if (!sameScope && (this.hasDirty || this.saving)) {
      this.error = this.$t('receivingReport.finishEditing')
      next(false)
      return
    }
    next()
    this.$nextTick(() => {
      // Rapid type changes can finish more than one navigation in the same tick.
      // Only the current route may restore its filters and selected type.
      if (this.disposed || this.$route !== to) return
      if (!sameScope) { this.invalidateScope(); this.initialize(); return }
      const sort = to.query.sort === 'number' ? 'number' : 'loss'
      const query = normalizeNumberQuery(to.query.q)
      const full = to.query.full === '1'
      if (sort !== this.sort || query !== this.searchQuery || full !== this.onlyFull) {
        this.sort = sort; this.numberQuery = query; this.onlyFull = full; this.presentationChanged()
      }
      const type = Number(to.query.type)
      if (type !== this.activeType && this.report?.types.some(column => column.betType === type)) this.selectType(type)
    })
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasDirty || this.saving) { this.error = this.$t('receivingReport.finishEditing'); next(false) } else next()
  },
  beforeDestroy() {
    this.disposed = true
    this.requestId += 1
    this.initializationId += 1
    this.roundRequestId += 1
    this.historyRequestId += 1
    clearTimeout(this.refreshTimer)
    document.removeEventListener('visibilitychange', this.scheduleRefresh)
    document.removeEventListener('transitionend', this.onNavbarTransition)
    document.removeEventListener('transitioncancel', this.onNavbarTransition)
    window.removeEventListener('beforeunload', this.beforeUnload)
    window.removeEventListener('resize', this.syncViewport)
    window.removeEventListener('scroll', this.queuePageMeasure)
    if (this.viewportFrame !== null) window.cancelAnimationFrame(this.viewportFrame)
    if (this.focusFrame !== null) window.cancelAnimationFrame(this.focusFrame)
    if (this.gridObserver) this.gridObserver.disconnect()
    if (this.typeSwipe) this.typeSwipe.destroy()
  },
  methods: {
    money,
    beforeUnload(event) {
      if (this.hasDirty || this.saving) { event.preventDefault(); Object.assign(event, { returnValue: '' }) }
    },
    dateLabel(value) { return value ? new Date(value).toLocaleDateString(this.$i18n.locale === 'th' ? 'th-TH' : 'en-GB', { timeZone: 'Asia/Bangkok' }) : '—' },
    timeLabel(value) { return value ? new Date(value).toLocaleString(this.$i18n.locale === 'th' ? 'th-TH' : 'en-GB', { timeZone: 'Asia/Bangkok' }) : '—' },
    historyTypeLabel(change) {
      const label = change.label || this.report?.types.find(type => type.betType === change.betType)?.label
      if (label) return this.$t(label)
      return change.betType === -1 ? this.$t('receivingReport.allTypes') : String(change.betType)
    },
    historyCapLabel(change, side) {
      const value = change[side]
      const exists = change[`${side}Exists`]
      if (exists === false || (exists == null && value == null)) return this.$t('receivingReport.inherit')
      const label = value == null || Number(value) < 0 ? this.$t('receivingReport.unlimited') : money(value)
      return change[`${side}Enabled`] === false ? `${label} (${this.$t('receivingReport.disabledCap')})` : label
    },
    responseError(response) {
      if (response?.status === 403 || response?.status === 401) return this.$t('receivingReport.denied')
      if (response?.data?.code === 'SOURCE_INCOMPLETE') return this.$t('receivingReport.incomplete')
      if (response?.data?.code === 'ROUND_NOT_FOUND') return this.$t('receivingReport.roundUnavailable')
      if (response?.data?.code === 'LOTTERY_NOT_FOUND') return this.$t('receivingReport.lotteryUnavailable')
      return this.$t('receivingReport.loadFailed')
    },
    async initialize() {
      const { userKey } = this
      this.initializationId += 1
      const initialization = this.initializationId
      this.initializing = true
      this.error = ''
      try {
        const res = await receivingService.context()
        if (this.disposed || userKey !== this.userKey || initialization !== this.initializationId) return
        if (res.status !== 200) { this.error = this.responseError(res); return }
        this.context = res.data
        this.lotteries = res.data.lotteries || []
        const q = this.$route.query
        this.sort = q.sort === 'number' ? 'number' : 'loss'
        this.numberQuery = normalizeNumberQuery(q.q)
        this.onlyFull = q.full === '1'
        this.activeType = Number(q.type) || 1
        const target = res.data.canSelectAgent ? q.agent || res.data.agent : res.data.agent
        this.selectedAgent = { code: target, username: target, label: target }
        this.lottery = q.huayId ? this.lotteries.find(v => v.huayId === q.huayId) || null : this.lotteries[0] || null
        if (q.huayId && !this.lottery) { this.error = this.$t('receivingReport.lotteryUnavailable'); return }
        await this.loadRounds(q.roundId)
      } catch (err) {
        if (!this.disposed && userKey === this.userKey && initialization === this.initializationId) this.error = this.responseError(err.response)
      } finally { if (!this.disposed && userKey === this.userKey && initialization === this.initializationId) this.initializing = false }
    },
    invalidateScope() {
      this.cancelSwipe()
      this.mobileTableMinHeight = 0
      this.requestId += 1
      this.historyRequestId += 1
      clearTimeout(this.refreshTimer)
      this.report = null
      this.explanationOpen = false
      this.pendingReport = null
      this.positions = {}
      this.scrollTop = 0
      this.drafts = {}
      this.originals = {}
      this.historyVisible = false
      this.history = []
      this.numberVisible = false
      this.selectedNumber = null
      this.message = ''
      this.error = ''
      this.loading = false
    },
    agentChanged() { this.invalidateScope(); this.fetchReport() },
    lotteryChanged() { this.invalidateScope(); this.loadRounds() },
    selectRound(value) { this.roundId = value || ''; this.roundChanged() },
    roundChanged() { this.invalidateScope(); this.fetchReport() },
    async loadRounds(preferred) {
      this.roundRequestId += 1
      const request = this.roundRequestId
      this.rounds = []
      this.roundId = ''
      if (!this.lottery) return
      this.loadingRounds = true
      try {
        const res = await receivingService.rounds(this.lottery.huayId, preferred)
        if (this.disposed || request !== this.roundRequestId) return
        if (res.status !== 200) { this.error = this.responseError(res); return }
        this.rounds = res.data || []
        this.roundId = preferred ? this.rounds.find(v => v.roundId === preferred)?.roundId || '' : this.rounds[0]?.roundId || ''
        if (preferred && !this.roundId) { this.error = this.$t('receivingReport.roundUnavailable'); return }
        await this.fetchReport()
      } catch (err) {
        if (!this.disposed && request === this.roundRequestId) this.error = this.responseError(err.response)
      } finally { if (!this.disposed && request === this.roundRequestId) this.loadingRounds = false }
    },
    syncRoute() {
      if (!this.lottery || !this.roundId) return
      const query = {
        agent: this.agent, huayId: this.lottery.huayId, roundId: this.roundId, sort: this.sort, type: String(this.activeType),
      }
      if (this.searchQuery) query.q = this.searchQuery
      if (this.onlyFull) query.full = '1'
      if (Object.keys(query).length !== Object.keys(this.$route.query).length
        || Object.keys(query).some(key => query[key] !== this.$route.query[key])) this.$router.replace({ name: 'receiving-report', query }).catch(() => {})
    },
    async fetchReport({ automatic = false } = {}) {
      if (!this.roundId || !this.lottery || !this.agent || this.saving || this.disposed) return
      clearTimeout(this.refreshTimer)
      this.requestId += 1
      const request = this.requestId
      const scope = this.scopeKey
      this.loading = true
      this.error = ''
      this.syncRoute()
      try {
        const res = await receivingService.read(this.lottery.huayId, this.roundId, {
          agent: this.agent, allNumbers: true,
        })
        if (this.disposed || request !== this.requestId || scope !== this.scopeKey) return
        if (res.status !== 200) {
          if ([401, 403].includes(res.status)) this.invalidateScope()
          this.error = this.responseError(res)
          return
        }
        if (!completeReceivingReport(res.data)) { this.error = this.$t('receivingReport.completeFailed'); return }
        // Never reorder a scrolled reader's rows or overwrite an edit that started during the request.
        const lostWriteAccess = this.report?.canEditCaps && !res.data.canEditCaps
        if (this.report && !lostWriteAccess && (this.hasDirty || this.historyVisible || this.numberVisible || this.swipePreviews.length
          || (automatic && (this.scrollTop > 0 || document.activeElement?.closest?.('.receiving-numbers'))))) {
          this.pendingReport = Object.freeze(res.data)
        } else await this.applyReport(res.data)
      } catch (err) {
        if (!this.disposed && request === this.requestId && scope === this.scopeKey) {
          if ([401, 403].includes(err.response?.status)) this.invalidateScope()
          this.error = this.responseError(err.response)
        }
      } finally {
        if (!this.disposed && request === this.requestId) { this.loading = false; this.scheduleRefresh() }
      }
    },
    async applyReport(report) {
      const position = this.capturePosition()
      const dirty = this.hasDirty
      // Reports are immutable snapshots. Avoid deep Vue observation of tens of thousands of numbers.
      this.report = Object.freeze(report)
      this.pendingReport = null
      if (!dirty || !report.canEditCaps) this.resetDrafts()
      if (!report.types.some(v => v.betType === this.activeType)) this.activeType = report.types[0]?.betType || 1
      this.syncRoute()
      await this.restorePosition(position)
    },
    async applyPending() {
      if (!this.pendingReport || this.hasDirty || this.saving || this.historyVisible || this.numberVisible || this.swipePreviews.length) return
      await this.applyReport(this.pendingReport)
      this.scheduleRefresh()
    },
    presentationChanged() {
      this.positions = {}
      this.restorePosition()
      this.syncRoute()
    },
    clearFilters() { this.numberQuery = ''; this.onlyFull = false; this.presentationChanged() },
    capturePosition() {
      const { grid } = this.$refs
      const viewport = this.tableViewport()
      const top = viewport?.scrollTop ?? this.scrollTop
      const left = grid?.scrollLeft || 0
      const columnIndex = grid ? Math.floor(left / (grid.querySelector('.receiving-type')?.offsetWidth || 234)) : 0
      const column = this.isMobile ? this.viewTypes.find(v => v.betType === this.activeType) : this.viewTypes[columnIndex]
      const hiddenSummary = Math.min(Math.max(0, top), this.flowingSummaryHeight())
      const rowTop = Math.max(0, top - hiddenSummary)
      return {
        type: column?.betType,
        number: column?.numbers[Math.floor(rowTop / RECEIVING_ROW_HEIGHT)]?.number,
        offset: rowTop % RECEIVING_ROW_HEIGHT,
        hiddenSummary,
        pageY: window.scrollY || 0,
        inTable: Boolean(viewport && viewport.scrollTop > 0 && viewport.viewportHeight > 0),
        top,
        left,
      }
    },
    async restorePosition(position) {
      const keepAnchor = position || {}
      const pageY = keepAnchor.pageY ?? (window.scrollY || 0)
      const inTable = position ? keepAnchor.inTable : (this.tableViewport()?.scrollTop || 0) > 0
      const scope = this.scopeKey
      const type = this.activeType
      await this.$nextTick()
      if (this.disposed || scope !== this.scopeKey || type !== this.activeType) return
      this.observeGrid()
      const { grid } = this.$refs
      if (!grid) { this.scrollTop = keepAnchor.top || 0; return }
      if (this.isMobile && inTable) {
        // A short category must not pull the selector down when the page shrinks.
        this.mobileTableMinHeight = Math.ceil((window.innerHeight || 600) - this.pageStickyTop() - this.saveBarHeight + this.flowingSummaryHeight())
        await this.$nextTick()
        if (this.disposed || scope !== this.scopeKey || type !== this.activeType || grid !== this.$refs.grid) return
      }
      const top = this.positionTop(keepAnchor)
      const viewport = this.tableViewport()
      window.scrollTo({ top: inTable && viewport ? Math.max(0, viewport.pageTop - viewport.stickyTop + top) : pageY, behavior: 'instant' })
      grid.scrollLeft = keepAnchor.left || 0
      this.onGridScroll()
      this.measureGrid()
    },
    positionTop(position) {
      const column = this.viewTypes.find(v => v.betType === position.type)
      const index = position.number == null ? -1 : column?.numbers.findIndex(v => v.number === position.number) ?? -1
      const distance = this.flowingSummaryHeight()
      const rowTop = index >= 0 ? index * RECEIVING_ROW_HEIGHT + (position.offset || 0) : 0
      const hiddenSummary = rowTop > 0 ? distance : Math.min(position.hiddenSummary || 0, distance)
      return index >= 0 ? rowTop + hiddenSummary : position.top || 0
    },
    typePosition(type, current) {
      const saved = this.positions[String(type)]
      const viewport = this.tableViewport()
      const inTable = current.inTable || Boolean(viewport && viewport.pageTop - current.pageY <= viewport.stickyTop + 1)
      const column = this.viewTypes.find(value => value.betType === type)
      const summary = this.flowingSummaryHeight()
      const readingRows = current.top >= summary
      const hiddenSummary = readingRows ? summary : Math.min(current.top, summary)
      const target = readingRows && saved ? { ...saved, hiddenSummary, top: Math.max(summary, saved.top) } : {
        type, number: column?.numbers[0]?.number, offset: 0, hiddenSummary, top: hiddenSummary, left: 0,
      }
      return { ...target, inTable, pageY: current.pageY }
    },
    swipePosition(type, current) {
      // Keep the saved row, but share the outgoing page's partial-row offset so
      // separators meet throughout the drag and stay there after the handoff.
      return { ...this.typePosition(type, current), offset: current.offset }
    },
    selectType(type, position) {
      if (type === this.activeType) return undefined
      const current = this.capturePosition()
      const renderedType = Number(this.$refs.grid?.querySelector('.receiving-type')?.dataset?.betType)
      // Do not overwrite a saved position for a type that has not rendered yet.
      if (!this.isMobile || renderedType === this.activeType) this.$set(this.positions, this.positionKey, current)
      this.activeType = type
      const restoration = this.isMobile ? this.restorePosition(position || this.typePosition(type, current)) : undefined
      this.syncRoute()
      return restoration
    },
    selectMobileType(type) {
      this.cancelSwipe()
      this.selectType(type)
    },
    stepMobileType(direction) {
      if (this.typeSwipe) this.typeSwipe.step(direction)
    },
    cancelSwipe() { if (this.typeSwipe) this.typeSwipe.cancel() },
    async prepareSwipe() {
      const { grid } = this.$refs
      if (!grid || !this.isMobile) return null
      this.measureGrid()
      const current = this.capturePosition()
      const index = this.viewTypes.findIndex(column => column.betType === this.activeType)
      this.swipeLayout = { width: grid.getBoundingClientRect().width, left: grid.scrollLeft, height: grid.getBoundingClientRect().height }
      this.swipePreviews = [-1, 1].flatMap(side => {
        const column = this.viewTypes[index + side]
        if (!column) return []
        const position = this.swipePosition(column.betType, current)
        return [{
          column, side, position, scrollTop: this.positionTop(position),
        }]
      })
      await this.$nextTick()
      return { width: this.swipeLayout.width }
    },
    swipePreviewStyle(preview, labels = false) {
      return {
        left: `${preview.side * this.swipeLayout.width}px`,
        top: labels ? '0px' : `${this.scrollTop - preview.scrollTop}px`,
        width: `${this.swipeLayout.width}px`,
      }
    },
    selectSwipeType(type) {
      const preview = this.swipePreviews.find(value => value.column.betType === type)
      const target = this.swipePosition(type, this.capturePosition())
      return this.selectType(type, preview ? { ...preview.position, inTable: target.inTable, pageY: target.pageY } : target)
    },
    ensureTypeSwipe() {
      const { tableSurface } = this.$refs
      if (!tableSurface || tableSurface === this.swipeElement) return
      if (this.typeSwipe) this.typeSwipe.destroy()
      this.swipeElement = tableSurface
      this.typeSwipe = receivingTypeSwipe(tableSurface, {
        enabled: () => this.isMobile && Boolean(this.report) && this.viewTypes.length > 1,
        activeType: () => this.activeType,
        types: () => this.viewTypes.map(column => column.betType),
        prepare: this.prepareSwipe,
        clear: () => { if (this.swipePreviews.length) this.swipePreviews = [] },
        select: this.selectSwipeType,
        nextTick: () => this.$nextTick(),
      })
    },
    headerColumnStyle(index) { return { width: `${this.columnWidths[index] || 260}px` } },
    syncViewport() {
      const mobile = window.innerWidth < 768
      if (mobile !== this.isMobile) {
        this.$set(this.positions, this.positionKey, this.capturePosition())
        this.isMobile = mobile
        this.restorePosition(this.positions[this.positionKey])
      } else this.measureGrid()
    },
    observeGrid() {
      this.ensureTypeSwipe()
      const { grid } = this.$refs
      if (!grid || grid === this.observedGrid) return
      if (this.gridObserver) this.gridObserver.disconnect()
      this.observedGrid = grid
      if (typeof ResizeObserver !== 'undefined') {
        this.gridObserver = new ResizeObserver(this.queuePageMeasure)
        this.gridObserver.observe(grid)
        // Changes above the table move its page origin without resizing the table.
        const panels = this.$el.querySelectorAll('.receiving-controls, .receiving-content')
        panels.forEach(panel => this.gridObserver.observe(panel))
      }
    },
    pageStickyTop() {
      const navbar = document.querySelector?.('.header-navbar')
      if (!navbar || !['fixed', 'sticky'].includes(window.getComputedStyle(navbar).position)) return 0
      const box = navbar.getBoundingClientRect()
      return box.height && box.bottom > 0 ? box.bottom + 8 : 0
    },
    onNavbarTransition(event) {
      // The type watcher can measure mid-transition, before the navbar settles.
      if (event.target?.matches?.('.header-navbar')) this.queuePageMeasure()
    },
    onReportFocus(event) {
      const cap = event.target.closest?.('.receiving-cap-row')
      if (!cap && !event.target.closest?.('.receiving-controls')) return
      if (this.focusFrame !== null) window.cancelAnimationFrame(this.focusFrame)
      const control = event.target
      this.focusFrame = window.requestAnimationFrame(() => {
        this.focusFrame = null
        if (this.disposed || document.activeElement !== control) return
        // Native focus scrolling does not account for the floating navigation bar.
        const box = control.getBoundingClientRect()
        const titleHeight = cap?.closest('.receiving-type').querySelector('.receiving-type-title').getBoundingClientRect().height || 0
        const top = this.pageStickyTop() + titleHeight + 8
        const bottom = window.innerHeight - 8
        const offset = box.top < top ? box.top - top : Math.max(0, box.bottom - bottom)
        if (offset) window.scrollTo({ top: (window.scrollY || 0) + offset, behavior: 'instant' })
      })
    },
    tableViewport() {
      const { grid } = this.$refs
      if (!grid?.getBoundingClientRect) return null
      const box = grid.getBoundingClientRect()
      const stickyTop = this.pageStickyTop()
      const top = box.top + grid.clientTop
      const pageTop = top + (window.scrollY || 0)
      return {
        ...receivingPageViewport(top, grid.clientHeight, (window.innerHeight || 600) - this.saveBarHeight, stickyTop),
        pageTop,
        stickyTop,
      }
    },
    queuePageMeasure() {
      if (this.disposed || this.viewportFrame !== null) return
      this.viewportFrame = window.requestAnimationFrame(() => {
        this.viewportFrame = null
        this.measureGrid()
      })
    },
    measureGrid() {
      const { grid, saveBar } = this.$refs
      if (!grid) return
      this.saveBarHeight = saveBar?.getBoundingClientRect().height || 0
      const viewport = this.tableViewport()
      if (!viewport) return
      this.scrollTop = viewport.scrollTop
      this.viewportHeight = viewport.viewportHeight
      this.tableInView = viewport.viewportHeight > 0
      if (this.mobileTableMinHeight && viewport.pageTop - (window.scrollY || 0) > window.innerHeight) this.mobileTableMinHeight = 0
      this.tableStickyTop = viewport.stickyTop
      const height = selector => grid.querySelector(selector)?.getBoundingClientRect().height || 0
      this.tableTitleHeight = height('.receiving-type-title')
      this.tableLabelsHeight = height('.receiving-labels-sticky')
      this.tableSummaryHeight = this.flowingSummaryHeight()
      const widths = Array.from(grid.querySelectorAll('.receiving-grid > .receiving-type'), column => column.getBoundingClientRect().width)
      if (widths.length !== this.columnWidths.length || widths.some((width, index) => width !== this.columnWidths[index])) {
        this.columnWidths = widths
        this.$nextTick(this.onGridScroll)
      }
      // A mobile swipe translates content without changing the table's width.
      this.horizontalWidth = this.isMobile ? Math.max(grid.clientWidth, grid.querySelector('.receiving-type')?.offsetWidth || 0) : grid.scrollWidth
      this.horizontalRange = Math.max(0, this.horizontalWidth - grid.clientWidth)
      this.hasHorizontalOverflow = this.horizontalWidth > grid.clientWidth + 1
    },
    onHorizontalWheel(event) {
      const { grid } = this.$refs
      if (!grid || this.isMobile || this.horizontalTimelineSupported || event.ctrlKey || !event.cancelable) return
      const delta = event.deltaX || (event.shiftKey ? event.deltaY : 0)
      if (!delta) return
      // Without scroll timelines, move every viewport in this input event so
      // native wheel scrolling cannot paint the body before its headers.
      event.preventDefault()
      const line = event.deltaMode === 1 ? parseFloat(window.getComputedStyle(grid).lineHeight) || 16 : 1
      grid.scrollLeft += delta * (event.deltaMode === 2 ? grid.clientWidth : line)
      this.onGridScroll()
      if (!event.shiftKey && event.deltaY) window.scrollBy({ top: event.deltaY * (event.deltaMode === 2 ? window.innerHeight : line), behavior: 'instant' })
    },
    onGridScroll() {
      const {
        grid, horizontalRail, titleViewport, labelsViewport,
      } = this.$refs
      if (!grid) return
      const viewports = [horizontalRail, titleViewport, labelsViewport]
      viewports.forEach(viewport => {
        if (viewport && viewport.scrollLeft !== grid.scrollLeft) viewport.scrollTo({ left: grid.scrollLeft, behavior: 'instant' })
      })
    },
    onHeaderScroll(event) { this.onRailScroll(event) },
    onRailScroll(event) {
      const { grid } = this.$refs
      if (grid && grid.scrollLeft !== event.target.scrollLeft) grid.scrollLeft = event.target.scrollLeft
    },
    flowingSummaryHeight() {
      const { grid } = this.$refs
      const height = selector => grid?.querySelector(selector)?.getBoundingClientRect?.().height || 0
      return Math.max(0, height('.receiving-type-heading') - height('.receiving-type-title') - height('.receiving-labels-sticky'))
    },
    setDraft(type, value) { this.$set(this.drafts, type, value); this.message = ''; this.scheduleRefresh() },
    resetDrafts() {
      this.originals = Object.fromEntries((this.report?.types || []).map(v => [v.betType, v.hasCapOverride ? capInput(v.capAmount) : '']))
      this.drafts = { ...this.originals }
      this.scheduleRefresh()
    },
    discardCaps() {
      if (this.saving) return
      this.resetDrafts()
      this.error = ''
      this.message = ''
    },
    scheduleRefresh() {
      clearTimeout(this.refreshTimer)
      if (this.autoRefresh && !this.disposed && !document.hidden && !this.hasDirty && !this.saving && !this.loading && !this.historyVisible && !this.numberVisible && this.report) {
        this.refreshTimer = setTimeout(() => this.fetchReport({ automatic: true }), 15000)
      }
    },
    async saveCaps() {
      if (!this.report?.canEditCaps || !this.hasDirty || this.saving || this.loading) return
      let caps
      try { caps = changedCaps(this.report.types, this.drafts, this.originals, this.report.agent, this.lottery.huayId) } catch {
        this.error = this.$t('receivingReport.invalidCap'); return
      }
      const scope = this.scopeKey
      this.saving = true
      let saved = false
      this.error = ''
      clearTimeout(this.refreshTimer)
      try {
        const res = await receivingService.saveCaps(this.report.agent, caps)
        if (this.disposed || scope !== this.scopeKey) return
        if (res.status !== 200) {
          if ([401, 403].includes(res.status)) this.invalidateScope()
          this.error = [401, 403].includes(res.status) ? this.responseError(res) : this.$t('receivingReport.saveFailed')
          return
        }
        this.originals = { ...this.drafts }
        this.message = this.$t('receivingReport.saved')
        saved = true
      } catch (err) {
        if (!this.disposed && scope === this.scopeKey) {
          if ([401, 403].includes(err.response?.status)) this.invalidateScope()
          this.error = [401, 403].includes(err.response?.status) ? this.responseError(err.response) : this.$t('receivingReport.saveFailed')
        }
      } finally {
        this.saving = false
        if (!this.disposed && scope === this.scopeKey && saved) await this.fetchReport()
      }
    },
    async openHistory() {
      if (!this.report) return
      this.historyRequestId += 1
      const request = this.historyRequestId
      this.historyVisible = true
      this.historyLoading = true
      this.historyError = ''
      this.history = []
      clearTimeout(this.refreshTimer)
      try {
        const res = await receivingService.history(this.lottery.huayId, this.report.agent)
        if (this.disposed || request !== this.historyRequestId) return
        if (res.status === 200) this.history = res.data
        else this.historyError = this.responseError(res)
      } catch (err) {
        if (!this.disposed && request === this.historyRequestId) this.historyError = this.responseError(err.response)
      } finally { if (!this.disposed && request === this.historyRequestId) this.historyLoading = false }
    },
    openNumber(column, number) {
      this.selectedNumber = number
      this.numberTitle = `${this.$t(column.label)} ${number.number}`
      this.numberVisible = true
      clearTimeout(this.refreshTimer)
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.receiving-report {
  --receiving-number-width: 26%;
  --receiving-accepted-width: 32%;
  --receiving-payout-width: 42%;
  color: var(--report-strong-text);
  .receiving-heading-icon { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 44px; height: 44px; color: var(--report-accent); background: var(--report-heading-icon-bg); }
  .receiving-heading-subtitle { color: var(--report-body-text); }
  .receiving-toolbar, .receiving-edit-row, .receiving-meta, .receiving-save-actions { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: .75rem; }
  .receiving-controls .form-group > label { display: flex; align-items: center; min-height: 24px; }
  .receiving-scope .vs__dropdown-toggle { height: $input-height; min-height: $input-height; }
  .receiving-scope .vs--loading .vs__dropdown-toggle { opacity: 1; }
  .receiving-scope .vs--loading .vs__open-indicator { opacity: 0; }
  .receiving-scope .vs__spinner { border-color: var(--report-border); border-left-color: var(--report-secondary-text); }
  .receiving-scope .vs__selected-options { flex-wrap: nowrap; min-width: 0; }
  .receiving-scope .vs__selected { min-width: 0; overflow: hidden; white-space: nowrap; }
  .receiving-lottery-option { display: inline-flex; align-items: center; gap: .625rem; min-width: 0; max-width: 100%; }
  .receiving-lottery-option-icon { width: 18px !important; height: 18px !important; border: 0; box-shadow: var(--report-flag-shadow); }
  .receiving-lottery-option-name { min-width: 0; white-space: normal; }
  .receiving-lottery-select .vs__selected .receiving-lottery-option-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .receiving-scope-label { display: flex; align-items: center; justify-content: space-between; gap: .5rem; width: 100%; }
  .receiving-read-only { display: inline-flex; align-items: center; gap: .35rem; padding: .15rem .45rem; border: 1px solid var(--report-border); border-radius: .357rem; background: var(--report-muted-bg); color: var(--report-secondary-text); font-size: .75rem; font-weight: 400; line-height: 1.4; white-space: nowrap; }
  .receiving-toolbar { align-items: flex-end; gap: 1rem 1.5rem; }
  .receiving-view-tools, .receiving-data-tools { display: flex; align-items: center; flex-wrap: wrap; gap: .75rem; min-width: 0; }
  .receiving-view-tools { flex: 1 1 630px; align-items: flex-end; row-gap: 1rem; }
  .receiving-data-tools { min-height: $input-height; margin-left: auto; gap: .5rem; }
  .receiving-data-tools .btn { display: inline-flex; align-items: center; justify-content: center; gap: .4rem; min-height: $input-height; white-space: nowrap; }
  .btn-outline-secondary { color: var(--report-secondary-text) !important; }
  .receiving-full-filter, .receiving-auto-refresh { white-space: nowrap; }
  .receiving-full-filter { display: flex; align-items: center; min-height: $input-height; margin-right: 0; }
  .receiving-auto-refresh { display: flex; align-items: center; width: 220px; min-height: $input-height; margin-right: .25rem; }
  .receiving-auto-refresh .custom-control-label { min-height: 1.7rem; }
  .receiving-auto-copy { display: flex; flex: 1; flex-direction: column; justify-content: center; min-width: 0; margin: 0; font-size: $input-font-size; line-height: 1.4; cursor: pointer; }
  .receiving-auto-copy small { display: block; color: var(--report-secondary-text); font-size: .857rem; line-height: 1.4; }
  .receiving-refreshing { animation: receiving-refresh-spin 1s linear infinite; }
  .receiving-sort { flex: 0 0 260px; min-width: 0; margin: 0; }
  .receiving-sort .v-select, .receiving-round-select {
    width: 100%;
    .vs__dropdown-toggle { height: $input-height; min-height: $input-height; padding: 0 $input-padding-x; }
    .vs__selected-options { align-items: center; flex-wrap: nowrap; min-width: 0; padding: 0; }
    &.vs--single .vs__selected {
      display: block;
      position: static;
      min-width: 0;
      max-width: 100%;
      margin: 0;
      padding: 0;
      font-size: $input-font-size;
      line-height: calc(#{$input-height} - 2px);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      opacity: 1;
      transform: none;
    }
    // Keep readonly select inputs available to keyboard users without reserving a second line.
    .vs__search { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; padding: 0; border: 0; opacity: 0; }
    .vs__actions { flex-shrink: 0; padding: 0 0 0 .5rem; }
    .vs__open-indicator { margin-top: 0; }
  }
  .receiving-round-select .vs__dropdown-option { white-space: normal; }
  .receiving-search-field { flex: 1 1 240px; max-width: 320px; min-width: 0; margin: 0; }
  .receiving-search { position: relative; }
  .receiving-search > svg { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: var(--report-secondary-text); pointer-events: none; }
  .receiving-search .form-control { padding-left: 2.75rem; }
  .receiving-search .form-control::placeholder, .receiving-cap-row input::placeholder { color: var(--report-secondary-text); opacity: 1; }
  .receiving-filter-summary { display: flex; align-items: center; flex-wrap: wrap; gap: .5rem 1rem; margin-bottom: .75rem; padding: .5rem .75rem; border: 1px solid var(--report-border); border-radius: .357rem; background: var(--report-muted-bg); }
  .receiving-filter-actions, .receiving-filter-count, .receiving-clear-filters { display: inline-flex; align-items: center; gap: .5rem; }
  .receiving-filter-actions { flex-wrap: wrap; }
  .receiving-filter-count { min-width: 0; color: var(--report-strong-text); font-size: .9rem; font-weight: 500; }
  .receiving-filter-count > svg { flex-shrink: 0; }
  .receiving-clear-filters { color: var(--report-action-text) !important; white-space: nowrap; }
  .receiving-filter-note { color: var(--report-secondary-text); font-size: .857rem; }
  .receiving-cap-hint { margin: 0 0 .75rem; font-size: .857rem; }
  .receiving-edit-row { margin-top: 1rem; padding-top: .75rem; min-height: $input-height; border-top: 1px solid var(--report-border); }
  // Stay above the app's scroll-to-top shortcut while cap actions are active.
  .receiving-edit-row-sticky { position: sticky; bottom: 0; z-index: 100; padding-bottom: calc(.75rem + env(safe-area-inset-bottom, 0px)); background: var(--report-panel-bg); }
  .receiving-edit-status { flex: 1 1 240px; min-width: 0; font-size: .9rem; overflow-wrap: anywhere; }
  .receiving-edit-status > [role='status'] { display: inline-flex; align-items: center; gap: .5rem; }
  .receiving-save-actions { margin-left: auto; flex-wrap: nowrap; align-items: stretch; }
  .receiving-save-actions .btn { display: inline-flex; align-items: center; justify-content: center; min-height: $input-height; }
  .receiving-save-actions .btn-flat-secondary { color: var(--report-secondary-text) !important; }
  .receiving-dismiss-save { padding: .5rem; min-width: 44px; }
  .receiving-summary-bar { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); padding: 0 0 1.4rem; border-bottom: 1px solid var(--report-border); }
  .receiving-summary-bar > div { padding: 0 1.5rem; }
  .receiving-summary-bar > div:first-child { padding-left: 0; }
  .receiving-summary-bar > div:last-child { padding-right: 0; }
  .receiving-summary-bar > div + div { border-left: 1px solid var(--report-border); }
  .receiving-summary-bar small, .receiving-summary-bar strong { display: block; }
  .receiving-summary-bar small { font-size: .857rem; color: var(--report-secondary-text); }
  .receiving-summary-bar strong { margin-top: .4rem; font-size: 1.5rem; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  .receiving-meta { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 400px); padding: .75rem 0; color: var(--report-secondary-text); font-size: .85rem; }
  .receiving-lottery-name { display: inline-flex; align-items: center; gap: .35rem; max-width: 100%; vertical-align: bottom; }
  .receiving-lottery-icon { width: 16px !important; height: 16px !important; border: 0; box-shadow: var(--report-flag-shadow); }
  .receiving-freshness { display: flex; align-items: center; justify-content: flex-end; gap: .75rem; min-height: $input-height; }
  .receiving-updated { min-width: 0; font-variant-numeric: tabular-nums; text-align: right; }
  .receiving-show-latest { flex-shrink: 0; white-space: nowrap; }
  .receiving-explanation { padding: 0 0 1rem; color: var(--report-secondary-text); font-size: .85rem; }
  .receiving-explanation-toggle { display: flex; align-items: center; gap: .35rem; width: 100%; padding: 0; border: 0; background: none; color: inherit; font: inherit; text-align: left; cursor: pointer; }
  .receiving-explanation-toggle:focus-visible { outline: 2px solid var(--report-accent); outline-offset: 3px; border-radius: .25rem; }
  .receiving-explanation-toggle svg { flex-shrink: 0; transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1); }
  .receiving-explanation-open { transform: rotate(90deg); }
  .receiving-explanation .collapsing { transition: height 220ms cubic-bezier(0.2, 0.8, 0.2, 1); }
  .receiving-explanation p { margin: 0; padding-top: .5rem; }
  // Overlay the native headers on their existing slots without adding height.
  // They must sit outside the horizontal scroller to stick to the page viewport.
  .receiving-table { position: relative; display: grid; grid-template-columns: minmax(0, 1fr); grid-template-rows: var(--receiving-title-height) var(--receiving-summary-height) var(--receiving-labels-height) minmax(0, 1fr); border: 1px solid var(--report-border); border-radius: .5rem; clip-path: inset(0 round .5rem); overflow-anchor: none; }
  .receiving-table > .receiving-grid-scroll { grid-column: 1; grid-row: 1 / -1; min-width: 0; border: 0; z-index: 0; }
  .receiving-sticky-titles, .receiving-sticky-labels { position: sticky; align-self: start; grid-column: 1; min-width: 0; pointer-events: none; }
  // Reserve the label height so the title stops above it at the table's end.
  .receiving-sticky-titles { grid-row: 1 / -1; top: var(--receiving-sticky-top); height: calc(var(--receiving-title-height) + var(--receiving-labels-height)); z-index: 3; }
  // The visible table starts at this sticky frame, including its outer borders.
  // Cover the old frame through the title's corners, then draw the anchored rim.
  // The table's clip keeps this cover away from preceding content before pinning.
  .receiving-sticky-titles::before { content: ''; position: absolute; top: calc(-1 * var(--receiving-sticky-top) - 1px); bottom: var(--receiving-labels-height); left: -1px; right: -1px; background: var(--report-panel-bg); pointer-events: auto; }
  .receiving-sticky-titles::after { content: ''; position: absolute; top: -1px; left: -1px; right: -1px; height: calc(var(--receiving-title-height) + 1px); border: 1px solid var(--report-border); border-bottom: 0; border-radius: .5rem .5rem 0 0; pointer-events: none; }
  .receiving-sticky-labels { grid-row: 3 / -1; top: calc(var(--receiving-sticky-top) + var(--receiving-title-height)); height: var(--receiving-labels-height); z-index: 2; }
  .receiving-header-viewport { overflow-x: auto; overflow-y: hidden; scrollbar-width: none; background: var(--report-panel-bg); pointer-events: auto; }
  .receiving-header-viewport::-webkit-scrollbar { height: 0; }
  .receiving-titles-viewport { position: relative; height: var(--receiving-title-height); border-radius: calc(.5rem - 1px) calc(.5rem - 1px) 0 0; }
  .receiving-labels-viewport { height: var(--receiving-labels-height); }
  .receiving-header-track, .receiving-header-window { display: contents; }
  .receiving-header-row { display: flex; width: max-content; min-width: 100%; }
  .receiving-header-cell { flex: 0 0 auto; }
  .receiving-header-cell + .receiving-header-cell { border-left: 1px solid var(--report-border); }
  .receiving-header-placeholder { visibility: hidden; }
  // The header windows stay still inside their native input scrollers. Their
  // contents follow the body's scroll timeline on the same compositor frame.
  .receiving-native-horizontal {
    timeline-scope: --receiving-x;
    > .receiving-grid-scroll { scroll-timeline: --receiving-x x; }
    .receiving-header-track { display: block; width: calc(100% + var(--receiving-horizontal-range)); }
    .receiving-header-window { display: block; position: sticky; left: 0; width: calc(100% - var(--receiving-horizontal-range)); overflow: clip; }
    .receiving-header-row { animation: receiving-horizontal-sync 1ms linear both; animation-timeline: --receiving-x; }
  }
  @keyframes receiving-horizontal-sync {
    from { transform: translateX(0); }
    to { transform: translateX(calc(-1 * var(--receiving-horizontal-range))); }
  }
  .receiving-mobile-title-placeholder { height: 44px; }
  .receiving-grid-scroll { overflow-x: auto; overflow-y: hidden; overflow-anchor: none; scrollbar-width: none; border: 1px solid var(--report-border); border-radius: .5rem; }
  .receiving-grid-scroll::-webkit-scrollbar { height: 0; }
  .receiving-horizontal-rail { position: sticky; z-index: 3; height: 14px; overflow-x: auto; overflow-y: hidden; border: 1px solid var(--report-border); border-top: 0; border-radius: 0 0 .35rem .35rem; background: var(--report-panel-bg); visibility: hidden; }
  .receiving-horizontal-rail-visible { visibility: visible; }
  .receiving-horizontal-rail:focus-visible { outline: 2px solid var(--report-accent); outline-offset: 2px; }
  .receiving-grid-scroll:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--report-accent); }
  .receiving-grid { position: relative; display: flex; align-items: stretch; }
  .receiving-type { flex: 1 0 260px; min-width: 260px; background: var(--report-panel-bg); }
  .receiving-type + .receiving-type { border-left: 1px solid var(--report-border); }
  .receiving-type-heading { position: relative; z-index: 2; background: var(--report-panel-bg); }
  .receiving-type-title, .receiving-labels-sticky { position: relative; background: var(--report-panel-bg); }
  .receiving-type-title { z-index: 2; box-shadow: 0 -1px 0 var(--report-panel-bg); }
  .receiving-labels-sticky { z-index: 1; }
  .receiving-type-navigation { display: flex; align-items: stretch; width: 100%; min-width: 0; min-height: 44px; background: var(--report-table-head-bg); }
  .receiving-type-step { display: inline-flex; flex: 0 0 32px; align-items: center; justify-content: center; min-height: 44px; padding: 0; border: 0; background: transparent; color: var(--report-label-text); cursor: pointer; transition: color 160ms ease, background-color 160ms ease, opacity 160ms ease; }
  .receiving-type-step:disabled { opacity: .28; cursor: default; }
  .receiving-type-step:not(:disabled):active { color: var(--report-action-text); background: var(--report-muted-bg); }
  @media (hover: hover) {
    .receiving-type-step:not(:disabled):hover { color: var(--report-action-text); background: var(--report-muted-bg); }
  }
  .receiving-type-step:focus-visible { outline: 2px solid var(--report-accent); outline-offset: -4px; border-radius: .35rem; }
  .receiving-type-tabs { display: flex; flex: 1 1 0; flex-wrap: nowrap; gap: 1rem; width: 0; min-width: 0; min-height: 44px; padding: 0; overflow-x: auto; overflow-y: hidden; overscroll-behavior-x: contain; scrollbar-width: none; }
  .receiving-type-tabs::-webkit-scrollbar { height: 0; }
  .receiving-type-tab { position: relative; display: inline-flex; flex: 0 0 auto; align-items: center; justify-content: center; gap: .3rem; min-width: 44px; min-height: 44px; padding: .5rem 0; border: 0; background: transparent; color: var(--report-secondary-text); font: inherit; font-size: .9rem; font-weight: 500; white-space: nowrap; cursor: pointer; transition: color 160ms ease; }
  .receiving-type-tab::after { content: ''; position: absolute; height: 2px; left: 0; right: 0; bottom: 3px; border-radius: 2px; background: var(--report-action-text); transform: scaleX(0); transition: transform 180ms ease; }
  .receiving-type-tab[aria-pressed="true"] { color: var(--report-action-text); }
  .receiving-type-tab[aria-pressed="true"]::after { transform: scaleX(1); }
  .receiving-type-tab small { font-size: .85em; font-variant-numeric: tabular-nums; }
  .receiving-type-tab:focus-visible { outline: 2px solid var(--report-accent); outline-offset: -3px; border-radius: .25rem; }
  .receiving-type-tab:disabled { cursor: default; }
  .receiving-type-swipe .receiving-swipe-area, .receiving-table-mobile .receiving-swipe-area,
  .receiving-table-mobile > .receiving-grid-scroll { touch-action: pan-y pinch-zoom; }
  .receiving-labels-viewport { position: relative; }
  .receiving-swipe-preview-viewport { position: absolute; top: 0; bottom: 0; overflow: hidden; pointer-events: none; z-index: 3; }
  .receiving-swipe-preview { position: absolute; overflow: hidden; background: var(--report-panel-bg); }
  .receiving-table-mobile.receiving-swipe-active .receiving-grid > .receiving-type,
  .receiving-table-mobile.receiving-swipe-active .receiving-labels-viewport .receiving-header-row,
  .receiving-table-mobile.receiving-swipe-active .receiving-swipe-preview { transform: translate3d(var(--receiving-swipe-x, 0px), 0, 0); will-change: transform; }
  .receiving-table-mobile.receiving-swipe-active > .receiving-grid-scroll { overflow-x: hidden; }
  .receiving-type-title h4 { padding: .8rem .75rem; margin: 0; text-align: center; background: var(--report-table-head-bg); color: var(--report-label-text); font-size: 1rem; font-weight: 600; line-height: 1.5; }
  .receiving-type-summary { margin: 0; padding: .7rem .75rem; }
  .receiving-type-summary > div { display: flex; align-items: center; justify-content: space-between; gap: .4rem; padding: .12rem 0; }
  .receiving-type-summary dt { color: var(--report-secondary-text); font-size: .857rem; font-weight: 400; white-space: nowrap; }
  .receiving-type-summary dd { margin: 0; white-space: nowrap; font-variant-numeric: tabular-nums; font-size: .95rem; }
  .receiving-cap-row { display: flex; align-items: center; justify-content: space-between; gap: .5rem; min-height: 4.5rem; padding: .75rem; border-top: 1px solid var(--report-border); }
  .receiving-cap-row label { display: flex; flex-direction: column; gap: .125rem; margin: 0; font-size: .9rem; font-weight: 500; line-height: 1.5; white-space: nowrap; }
  .receiving-cap-row label small { font-size: .857rem; font-weight: 400; line-height: 1.5; }
  .receiving-cap-row input { width: 105px; min-width: 0; text-align: right; }
  .receiving-cap-row input::placeholder { color: var(--report-strong-text); }
  .receiving-cap-value { font-size: 1rem; font-weight: 500; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .receiving-cap-dirty { color: var(--report-warning-text); background: var(--report-match-bg); border-color: var(--report-warning-text); }
  .receiving-counts { display: grid; grid-template-columns: repeat(2, max-content); gap: .25rem .75rem; padding: .6rem .75rem; border-top: 1px solid var(--report-border); color: var(--report-secondary-text); font-size: .857rem; line-height: 1.5; }
  .receiving-counts span { white-space: nowrap; }
  .receiving-counts > span:last-of-type { grid-column: 1 / -1; }
  .receiving-counts strong { font-weight: 500; }
  // Match the bill report's 2px divider without changing the header's geometry.
  .receiving-grid-labels { display: grid; grid-template-columns: var(--receiving-number-width) var(--receiving-accepted-width) var(--receiving-payout-width); background: var(--report-table-head-bg); color: var(--report-label-text); padding: .75rem 0; font-size: var(--report-header-font-size); font-weight: 500; line-height: var(--report-header-line-height); box-shadow: inset 0 -2px 0 var(--report-border); }
  .receiving-grid-labels span { padding: 0 .5rem; white-space: nowrap; }
  .receiving-grid-labels span:not(:first-child) { text-align: right; }
  .receiving-numbers { --report-danger-text: var(--report-danger-badge-text); table-layout: fixed; border-collapse: collapse; width: 100%; font-variant-numeric: tabular-nums; font-size: .95rem; }
  .receiving-numbers tr[data-number] { height: 56px; }
  .receiving-numbers td { position: relative; box-sizing: border-box; padding: .5rem; height: 56px; border-bottom: 1px solid var(--report-border); vertical-align: middle; white-space: nowrap; }
  .receiving-numbers .receiving-spacer td { padding: 0; border: 0; line-height: 0; font-size: 0; }
  .receiving-number-column { width: var(--receiving-number-width); }
  .receiving-accepted-column { width: var(--receiving-accepted-width); }
  .receiving-payout-column { width: var(--receiving-payout-width); }
  .receiving-numbers td:not(:first-child) { text-align: right; }
  .receiving-money { display: flex; justify-content: flex-end; }
  .receiving-full-row { background: var(--report-detail-bg); }
  .receiving-number, .receiving-number-button { color: var(--report-strong-text); font-size: .95rem; font-weight: 600; }
  .receiving-number-button { border: 0; background: none; padding: 0; color: var(--report-action-text); text-decoration: underline; }
  .receiving-number-state, .receiving-amount-state { position: absolute; bottom: 3px; font-size: .786rem; line-height: 14px; }
  .receiving-number-state { left: .5rem; }
  .receiving-amount-state { right: .5rem; }
  .receiving-winner { font-size: inherit; line-height: inherit; padding: 0 3px; }
  .receiving-numbers .receiving-empty { text-align: center; white-space: normal; color: var(--report-secondary-text); height: 90px; }
  .receiving-skeleton-text { color: transparent !important; background-color: var(--report-border); border-radius: .25rem; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
  .receiving-skeleton-text *, .receiving-skeleton-control, .receiving-skeleton-control * { color: transparent !important; }
  .receiving-skeleton-control { background-color: var(--report-border) !important; border-color: var(--report-border) !important; }
  // Metadata arrives in stages; keep its placeholders stable and inherit the real line height.
  .receiving-loading-scope { width: 14rem; max-width: 100%; }
  .receiving-loading-updated { width: 16rem; max-width: 100%; }
  .receiving-loading-scope .receiving-skeleton-text, .receiving-loading-updated .receiving-skeleton-text { display: block; }
  .receiving-loading-grid { overflow: hidden; }
  @keyframes receiving-refresh-spin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) {
    .receiving-explanation .collapsing, .receiving-explanation-toggle svg { transition: none; }
    .receiving-refreshing { animation: none; }
    .receiving-type-tab, .receiving-type-tab::after, .receiving-type-step { transition: none; }
  }
  @media (max-width: 767px) {
    .receiving-controls > .card-body, .receiving-content > .card-body { padding: 1rem; }
    .receiving-summary-bar { grid-template-columns: repeat(2,minmax(0,1fr)); gap: .75rem 0; }
    .receiving-summary-bar > div:nth-child(3) { padding-left: 0; border-left: 0; }
    .receiving-summary-bar > div:nth-child(2) { padding-right: 0; }
    .receiving-summary-bar strong { font-size: 1.2rem; }
    .receiving-view-tools { flex-basis: 100%; }
    .receiving-search-field, .receiving-sort { flex-basis: 100%; max-width: none; }
    .receiving-data-tools { display: grid; grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr); width: 100%; gap: .75rem; }
    .receiving-auto-refresh { grid-column: 1 / -1; margin: 0; }
    .receiving-data-tools .btn { padding-left: .5rem; padding-right: .5rem; }
    .receiving-edit-status { flex-basis: 100%; }
    .receiving-save-actions { width: 100%; }
    .receiving-save-actions .btn { flex: 1; min-height: 44px; padding-left: .5rem; padding-right: .5rem; }
    .receiving-edit-row-saved .receiving-edit-status { flex: 1 1 0; }
    .receiving-edit-row-saved .receiving-save-actions { width: auto; }
    .receiving-meta { grid-template-columns: minmax(0, 1fr); }
    .receiving-freshness { justify-content: space-between; min-height: 3.7rem; }
    .receiving-updated { text-align: left; }
    .receiving-type { width: 100%; min-width: 18rem; border-left: 0; }
    .receiving-grid { display: block; }
    .receiving-cap-row input { font-size: 16px; }
  }
}
.receiving-history-entry { border-bottom: 1px solid var(--report-border); padding: .75rem 0; }
.receiving-parts { font-variant-numeric: tabular-nums; }
body:has(.receiving-report .receiving-edit-row-sticky) .btn-scroll-to-top { display: none; }
</style>
